import React, { useState, useEffect, useContext } from "react";
import ArticleDataService from "../services/ArticleService";
import ImageService from '../services/ImageService';
import { ApplicationContext } from "./ApplicationContext";
import "./ShopComponent.css";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Credits from "./Credits";
import log from "../services/logger";
import { addQuantity, removeQuantity } from "./utils";
import { Button, Card, Input } from './common/index';
import { gridStyles } from '../styles/tailwind.classes';

const ShopComponent = (props) => {
  const [articles, setArticles] = useState([]);
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState("");
  const { loggedInClient, addOrUpdateBasketItem } = useContext(ApplicationContext);
  const [quantities, setQuantities] = useState({});

  useEffect(() => {
    retrieveArticles();
  }, []);

  const retrieveArticles = () => {
    ArticleDataService.getAll()
      .then((response) => {
        const allArticles = response.data;
        const articles = allArticles.filter((article) => article.isVisible === true);

        // First, sort articles by ID treating ID as a number
        articles.sort((a, b) => Number(a.article_id) - Number(b.article_id));

        // Next, group sorted articles by brandName
        let groups = articles.reduce((acc, article) => {
          const brandName = article.brand.brandName;
          if (!acc[brandName]) {
            acc[brandName] = [];
          }
          acc[brandName].push(article);
          return acc;
        }, {});

        // Flatten the grouped articles to get the final sorted list
        const sortedArticles = Object.keys(groups)
          .sort()
          .flatMap((brandName) => groups[brandName]);

        setArticles(sortedArticles);
        setFilteredArticles(sortedArticles);
        setBrands(Object.keys(groups).sort());
      })
      .catch((e) => {
        log.info(e);
      });
  };

  const addToBasket = (article, quantity) => {
    if (
      quantity > 0 &&
      isValidQuantity(quantity, article.startingQuantityDemand, article.minimumQuantityDemand)
    ) {
      addOrUpdateBasketItem(article, quantity);
      setQuantities((prevQuantities) => ({
        ...prevQuantities,
        [article.article_id]: "",
      }));
      toast.success("Uspešno dodavanje artikla " + article.name + " u korpu!");
    } else {
      toast.error("Uneta količina nije validna.");
    }
  };

  const handleBrandFilterChange = (brandName) => {
    setSelectedBrand(brandName);
    if (brandName) {
      if (brandName === "ACTIVE PHARMA") {
        setFilteredArticles(
          articles.filter((article) => article.brand.brandName.startsWith(brandName))
        );
      } else {
        setFilteredArticles(
          articles.filter((article) => article.brand.brandName === brandName)
        );
      }
    } else {
      setFilteredArticles(articles);
    }
  };

  const numberFormatter = new Intl.NumberFormat("sr-RS", {
    style: "currency",
    currency: "RSD",
  });

  const discountedPrice = (price, discount) => {
    return (Number(price) * (1 - Number(discount / 100))).toFixed(2);
  };

  const handleQuantityChange = (id, value) => {
    setQuantities((prevQuantities) => ({
      ...prevQuantities,
      [id]: value,
    }));
  };

  const handleValueValidation = (id, value, startingQuantityDemand, minimumQuantityDemand) => {
    let newValue = value;
    if (value === "") {
      newValue = "";
    } else {
      newValue = getValidQuantity(value, startingQuantityDemand, minimumQuantityDemand);
    }

    setQuantities((prevQuantities) => ({
      ...prevQuantities,
      [id]: newValue,
    }));
  };

  function isValidQuantity(value, s, m) {
    value = Number(value);
    if (isNaN(value) || value < s) {
      return false;
    }
    const tolerance = 0.001;
    // Check if value is equal to starting quantity
    if (Math.abs(value - s) < tolerance) {
      return true;
    }
    // Check if value is divisible by minimum quantity
    if (value % m < tolerance || Math.abs((value % m) - m) < tolerance) {
      return true;
    }
    return false;
  }

  function getValidQuantity(value, s, m) {
    value = Number(value);
    if (isNaN(value) || value < s) {
      return s;
    }
    if (Math.abs(value - s) < 0.001) {
      return s;
    }
    if (value >= s && (value % m < 0.001 || Math.abs((value % m) - m) < 0.001)) {
      return value;
    }
    // Adjust upwards to the next multiple of m
    const n = Math.ceil(value / m);
    const newValue = n * m;
    if (newValue < s) {
      return s;
    }
    return Number(newValue.toFixed(2));
  }

  const formatExpiryTime = (minutes) => {
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    let result = "";
    if (hrs > 0) {
      result += `${hrs} h `;
    }
    if (mins > 0) {
      result += `${mins} min`;
    }
    return result.trim();
  };

  const handleDecreaseQuantity = (article) => {
    const currentQuantity = quantities[article.article_id] || "";
    const currentQty = Number(currentQuantity) || 0;
    const value = removeQuantity(currentQty, article);
    handleQuantityChange(article.article_id, value.toString());
    handleValueValidation(
      article.article_id,
      value,
      article.startingQuantityDemand,
      article.minimumQuantityDemand
    );
  };

  const handleIncreaseQuantity = (article) => {
    const currentQuantity = quantities[article.article_id] || "";
    const currentQty = Number(currentQuantity) || 0;
    const value = addQuantity(currentQty, article);
    handleQuantityChange(article.article_id, value.toString());
    handleValueValidation(
      article.article_id,
      value,
      article.startingQuantityDemand,
      article.minimumQuantityDemand
    );
  };

  const canDecrease = (article) => {
    const currentQuantity = quantities[article.article_id] || "";
    return !(
      currentQuantity === "" ||
      Number(currentQuantity) <= article.startingQuantityDemand
    );
  };

  const checkValidQuantity = (article) => {
    const currentQuantity = quantities[article.article_id] || "";
    return currentQuantity && isValidQuantity(
      currentQuantity,
      article.startingQuantityDemand,
      article.minimumQuantityDemand
    );
  };

  return (
    <div className="w-full max-w-7xl mx-auto px-4">
      <div className="flex flex-col w-full">
        <div className="mb-8">
          <div className="max-w-[250px] p-4">
            <h2 className="text-lg font-semibold mb-4">Filtriranje po brendu</h2>
            <select
              value={selectedBrand}
              onChange={(e) => handleBrandFilterChange(e.target.value)}
              className="w-full p-2 border rounded-lg"
            >
              <option value="">Svi brendovi</option>
              {brands.map((brandName, index) => (
                <option key={index} value={brandName}>
                  {brandName}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className={gridStyles.responsive}>
          {filteredArticles.map((article) => (
            <Card
              key={article.article_id}
              variant={article.isActive ? 'default' : 'inactive'}
              className={!article.isActive ? 'opacity-60' : ''}
            >
              <div className="p-4 space-y-4">
                <div className="h-48 flex items-center justify-center">
                  <LazyLoadImage
                    src={ImageService.findArticleImage(article)}
                    alt={article.name}
                    className={`max-h-full max-w-full object-contain ${!article.isActive ? 'grayscale' : ''}`}
                  />
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                      {article.code}
                    </span>
                    {!article.isActive && (
                      <span className="px-2 py-1 bg-red-100 text-red-800 text-sm rounded-full">
                        Neaktivan
                      </span>
                    )}
                  </div>
                  
                  <h3 className="font-bold text-md h-12 line-clamp-2">
                    {article.name}
                  </h3>
                  
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="secondary"
                      onClick={() => handleDecreaseQuantity(article)}
                      disabled={!article.isActive || !canDecrease(article)}
                    >
                      -
                    </Button>
                    
                    <Input
                      type="number"
                      value={quantities[article.article_id] || ''}
                      onChange={(e) => handleQuantityChange(article.article_id, e.target.value)}
                      className="text-center"
                      disabled={!article.isActive}
                    />
                    
                    <Button
                      variant="secondary"
                      onClick={() => handleIncreaseQuantity(article)}
                      disabled={!article.isActive}
                    >
                      +
                    </Button>
                  </div>

                  <Button
                    variant="success"
                    disabled={!article.isActive || !checkValidQuantity(article)}
                    onClick={() => addToBasket(article, quantities[article.article_id])}
                    className="w-full"
                  >
                    {article.isActive ? 'Dodaj' : 'Nije dostupno'}
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
        <Credits />
      </div>
    </div>
  );
};

export default ShopComponent;
